import React from 'react';
import logger from './logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to our logging system
    logger.error('React Error Boundary caught an error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorBoundary: this.constructor.name
    }, 'ERROR_BOUNDARY');

    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Send to error tracking service (e.g., Sentry)
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        }
      });
    }
  }

  handleReset = () => {
    logger.userAction('error_boundary_reset');
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;
      
      // If a custom fallback component is provided, use it
      if (Fallback) {
        return (
          <Fallback 
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onReset={this.handleReset}
          />
        );
      }

      // Default error UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h2>🚨 エラーが発生しました</h2>
            <p>申し訳ございません。予期しないエラーが発生しました。</p>
            
            <div className="error-actions">
              <button 
                onClick={this.handleReset}
                className="error-retry-btn"
              >
                再試行
              </button>
              <button 
                onClick={() => window.location.reload()}
                className="error-reload-btn"
              >
                ページを再読み込み
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <details className="error-details">
                <summary>開発者向け詳細情報</summary>
                <div className="error-info">
                  <h4>Error:</h4>
                  <pre>{this.state.error && this.state.error.toString()}</pre>
                  
                  <h4>Component Stack:</h4>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                  
                  <h4>Stack Trace:</h4>
                  <pre>{this.state.error && this.state.error.stack}</pre>
                </div>
              </details>
            )}
          </div>

          <style jsx>{`
            .error-boundary {
              min-height: 400px;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 20px;
              background: #f8f9fa;
            }
            
            .error-boundary-content {
              max-width: 600px;
              text-align: center;
              background: white;
              padding: 40px;
              border-radius: 12px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            }
            
            .error-boundary-content h2 {
              color: #dc3545;
              margin-bottom: 16px;
            }
            
            .error-boundary-content p {
              color: #666;
              margin-bottom: 24px;
              font-size: 16px;
            }
            
            .error-actions {
              display: flex;
              gap: 12px;
              justify-content: center;
              margin-bottom: 24px;
            }
            
            .error-retry-btn,
            .error-reload-btn {
              padding: 12px 24px;
              border: none;
              border-radius: 8px;
              cursor: pointer;
              font-size: 16px;
              transition: background-color 0.2s;
            }
            
            .error-retry-btn {
              background: #007bff;
              color: white;
            }
            
            .error-retry-btn:hover {
              background: #0056b3;
            }
            
            .error-reload-btn {
              background: #6c757d;
              color: white;
            }
            
            .error-reload-btn:hover {
              background: #545b62;
            }
            
            .error-details {
              text-align: left;
              margin-top: 24px;
              border: 1px solid #ddd;
              border-radius: 8px;
              padding: 16px;
            }
            
            .error-details summary {
              cursor: pointer;
              font-weight: 600;
              margin-bottom: 12px;
            }
            
            .error-info h4 {
              margin: 16px 0 8px 0;
              color: #333;
            }
            
            .error-info pre {
              background: #f8f9fa;
              padding: 12px;
              border-radius: 4px;
              overflow-x: auto;
              font-size: 12px;
              line-height: 1.4;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;